import {
  GameJoinResponse,
  SelectionUpdateObj,
  GameStatistics,
  MiniLeagueResponse,
  PartnerPageData,
  PopUpContent,
  PopUpData,
  ScoringWord,
  SelectionRecord,
  SiteContent,
  SiteContentType,
  UserInfo,
  UserProfile,
  LeaderboardResponse,
  GameINFOV2,
  LeagueItem,
  Workflow,
  Answer,
  GameType,
  Round,
  RoundWithUserData,
  SelectionsByMatch,
  Addresses,
  PositionData,
  RegisterDTO,
  GolfData,
  AdditionalQuestion,
  AdditionalAnswer,
  ExternalLoginRequest,
  ExternalLoginResponse,
  CharityPageConsentDecision,
  CharityPageConsentButtonClicked,
  UnitMatchStats,
} from 'types/types'
import { UserEmailPreferencesValidation } from '@/lib/validations'
import { z } from 'zod'
import { PartnerGamesResponse } from 'hooks/usePartnerGames'
import { AuthBEAPIClient, BEAPIClient, handleResponseSuccess } from './BEClient'
import { API_ENDPOINTS, GameStatusType } from './constants'

export const UserApi = {
  getMySelections: (gameId: number, roundId: number | null) => {
    return AuthBEAPIClient.get<SelectionRecord>(API_ENDPOINTS.GET_MY_SELECTIONS(gameId), {
      params: { round_id: roundId },
    }).then(handleResponseSuccess)
  },
  getUserSelections: (gameId: number, userId: number, roundId: number | null) => {
    return AuthBEAPIClient.get<SelectionRecord>(API_ENDPOINTS.GET_USER_SELECTIONS(gameId, userId), {
      params: { round_id: roundId },
    }).then(handleResponseSuccess)
  },

  getSelectionsByMatch: (
    gameId: number,
    matchId: number | undefined,
    limit: number,
    offset: number,
  ) => {
    return AuthBEAPIClient.get<SelectionsByMatch>(
      API_ENDPOINTS.GET_SELECTIONS_BY_MATCH(gameId, matchId),
      { params: { limit, offset } },
    ).then(handleResponseSuccess)
  },
  joinGame: (
    gameId: number,
    userPredictions: SelectionUpdateObj[],
    tiebreakers: Array<{ round_id: number; answer: number | null }> | undefined,
  ) => {
    return AuthBEAPIClient.post<GameJoinResponse>(API_ENDPOINTS.JOIN_GAME(gameId), {
      selections: userPredictions,
      tiebreakers,
    }).then(handleResponseSuccess)
  },

  getProfile: () => {
    return AuthBEAPIClient.get<UserInfo>(API_ENDPOINTS.GET_USER_PROFILE).then(handleResponseSuccess)
  },
  checkEmail: (email: string, organization_id: string) => {
    return BEAPIClient.get<{ nickname: string | null }>(API_ENDPOINTS.GET_USER_EMAIL, {
      params: { email, organization_id },
    }).then(handleResponseSuccess)
  },
  getGameInfo: (gameId: number) => {
    return AuthBEAPIClient.get<GameINFOV2>(API_ENDPOINTS.GET_GAME_INFO_V2(gameId)).then(
      handleResponseSuccess,
    )
  },
  getGetAdditionalQuestions: (place: string, partner_domain: string) => {
    return AuthBEAPIClient.get<AdditionalQuestion[]>(API_ENDPOINTS.GET_ADDITIONAL_QUESTIONS, {
      params: { place, partner_domain },
    }).then(handleResponseSuccess)
  },
  setAdditionalAnswer: (data: AdditionalAnswer) => {
    return AuthBEAPIClient.post<AdditionalAnswer>(
      API_ENDPOINTS.GET_ADDITIONAL_QUESTIONS,
      data,
    ).then(handleResponseSuccess)
  },
  getLeaderbard: (
    gameId: number,
    roundId: number | null,
    limit: number,
    offset: number,
    search?: string,
  ) => {
    return BEAPIClient.get<LeaderboardResponse>(API_ENDPOINTS.GET_GAME_LEADERBOARD(gameId), {
      params: { round_id: roundId, limit, offset, search },
    }).then(handleResponseSuccess)
  },
  getScoreWording: (
    sportId: number,
    points: number,
    selectionType: keyof typeof GameType,
    tournamentId: number,
    gameId: number,
    roundId: number | undefined,
  ) => {
    return BEAPIClient.get<ScoringWord>(API_ENDPOINTS.GET_SCORE_WORDING, {
      params: {
        sport_id: sportId,
        points,
        selection_type: selectionType,
        tournament_id: tournamentId,
        game_id: gameId,
        round_id: roundId,
      },
    }).then(handleResponseSuccess)
  },
  setGamePredictions: (
    gameId: number,
    predictions: SelectionUpdateObj[],
    tiebreakers: Array<{ round_id: number; answer: number | null }> | undefined,
  ) => {
    return AuthBEAPIClient.patch(API_ENDPOINTS.GET_GAME_SELECTIONS(gameId), {
      selections: predictions,
      tiebreakers,
    }).then(handleResponseSuccess)
  },
  setTiebreaker: (gameId: number, answer: number | undefined) => {
    return AuthBEAPIClient.patch(API_ENDPOINTS.SET_GAME_TIEBREAKER(gameId), {
      answer,
    }).then(handleResponseSuccess)
  },
  getPopUpData: (popUpData: PopUpData) => {
    return AuthBEAPIClient.get<PopUpContent[]>(API_ENDPOINTS.GET_POP_UP_DATA, {
      params: popUpData,
    }).then(handleResponseSuccess)
  },
  getPageStyles: (subdomain: string) => {
    return BEAPIClient.get<PartnerPageData>(API_ENDPOINTS.GET_PAGE_STYLES(subdomain)).then(
      handleResponseSuccess,
    )
  },
  getGameStats: (params: { statistics_type: string; entity_id: number; game_id: number }) => {
    return BEAPIClient.get<GameStatistics[]>(API_ENDPOINTS.GET_GAME_STATS, {
      params,
    }).then(handleResponseSuccess)
  },
  getGameStatsBatch: (params: { statistics_type: string; entity_ids: string; game_id: number }) => {
    // Unlike the singular entity_id call above, passing entity_ids (plural)
    // makes the backend return stats pre-grouped by match id instead of a
    // flat array: { [matchId: string]: GameStatistics[] }.
    return BEAPIClient.get<Record<string, GameStatistics[]>>(API_ENDPOINTS.GET_GAME_STATS, {
      params,
    }).then(handleResponseSuccess)
  },
  getMatchUnitStats: (matchId: number) => {
    return BEAPIClient.get<UnitMatchStats[]>(API_ENDPOINTS.GET_MATCH_STATS(matchId)).then(
      handleResponseSuccess,
    )
  },
  updateUserProfile: (profileData: Partial<UserProfile>) => {
    return AuthBEAPIClient.patch<UserProfile>(API_ENDPOINTS.UPDATE_USER_PROFILE, profileData).then(
      handleResponseSuccess,
    )
  },
  getSiteContent: (partnerDomain: string, contentType: SiteContentType) => {
    return BEAPIClient.get<SiteContent>(API_ENDPOINTS.GET_SITE_CONTENT, {
      params: { partner_domain: partnerDomain, content_type: contentType },
    }).then(handleResponseSuccess)
  },

  createMiniLeague: (
    gameId: number,
    miniLeagueName: string,
    miniLeagueDescription: string,
    image: File | null,
    logo: File | null = null,
  ) => {
    const form = new FormData()

    if (image) {
      form.append('image', image)
    }
    if (logo) {
      form.append('logo', logo)
    }
    const hasMultipart = image !== null || logo !== null
    return AuthBEAPIClient.post<MiniLeagueResponse>(
      `${API_ENDPOINTS.CREATE_MINI_LEAGUE(gameId)}?${new URLSearchParams({
        mini_league_name: miniLeagueName,
        mini_league_description: miniLeagueDescription,
      })}`,
      hasMultipart ? form : null,
      {
        headers: {
          'Content-Type': hasMultipart ? 'multipart/form-data' : null,
          timeout: hasMultipart ? 30000 : 5000,
        },
      },
    ).then(handleResponseSuccess)
  },
  editMiniLeague: (miniLeagueId: number, name?: string, description?: string) => {
    return AuthBEAPIClient.patch<MiniLeagueResponse>(
      API_ENDPOINTS.EDIT_MINI_LEAGUE(miniLeagueId),
      { name, description }, // body payload
    ).then(handleResponseSuccess)
  },
  joinToMiniLeague: (miniLeagueId: number) => {
    return AuthBEAPIClient.post<MiniLeagueResponse>(
      API_ENDPOINTS.JOIN_TO_MINI_LEAGUE(miniLeagueId),
    ).then(handleResponseSuccess)
  },
  getMyLeagues: (gameId: number) => {
    return AuthBEAPIClient.get<LeagueItem[]>(API_ENDPOINTS.GET_MY_LEAGUES(gameId)).then(
      handleResponseSuccess,
    )
  },
  getAllMiniLeagues: (gameId: number, pin?: string) => {
    return BEAPIClient.get<{ public: LeagueItem[]; private: LeagueItem[] }>(
      API_ENDPOINTS.GET_ALL_MINI_LEAGUES(gameId),
      { params: { invite_code: pin } },
    ).then(handleResponseSuccess)
  },
  getIfDataCollected: (gameId: number) => {
    return AuthBEAPIClient.get<{ collected: boolean }>(API_ENDPOINTS.GET_IF_DATA_COLLECTED, {
      params: { game_id: gameId },
    }).then(handleResponseSuccess)
  },
  setIsDataCollected: (gameId: number) => {
    return AuthBEAPIClient.post<{ collected: boolean }>(
      API_ENDPOINTS.SET_IF_DATA_COLLECTED(gameId),
    ).then(handleResponseSuccess)
  },
  getPositionData: (gameId: number, roundId: number | undefined) => {
    return AuthBEAPIClient.get<PositionData>(API_ENDPOINTS.GET_POSITION_DATA(gameId), {
      params: { round_id: roundId },
    }).then(handleResponseSuccess)
  },
  removeUserFromMiniLeague: (miniLeagueId: number, userId: number) => {
    return AuthBEAPIClient.delete<{ OK: boolean }>(
      API_ENDPOINTS.REMOVE_USER_FROM_MINI_LEAGUE(miniLeagueId),
      {
        params: { user_id: userId },
      },
    ).then(handleResponseSuccess)
  },
  removeMiniLeague: (miniLeagueId: number) => {
    return AuthBEAPIClient.patch<{ OK: boolean }>(
      API_ENDPOINTS.REMOVE_MINI_LEAGUE(miniLeagueId),
    ).then(handleResponseSuccess)
  },
  isUserJoined: (gameId: number) => {
    return AuthBEAPIClient.get<{
      is_user_joined: boolean
      next_round_is_user_joined?: boolean
    }>(API_ENDPOINTS.IS_USER_JOINED(gameId), {}).then(handleResponseSuccess)
  },
  getRoundById: (roundId: number | null) => {
    if (!roundId) return null

    return BEAPIClient.get<Round>(API_ENDPOINTS.GET_ROUND_BY_ID(roundId)).then(
      handleResponseSuccess,
    )
  },
  getRoundsByGame: (gameId: number, isAuthenticated: boolean) => {
    const client = isAuthenticated ? AuthBEAPIClient : BEAPIClient
    return client
      .get<RoundWithUserData[]>(API_ENDPOINTS.GET_ROUNDS_BY_GAME(gameId), { timeout: 15000 })
      .then(handleResponseSuccess)
  },

  getLeaderboardWithCelebrities: (
    gameId: number,
    roundId: number | null,
    limit: number,
    offset: number,
  ) => {
    return BEAPIClient.get<LeaderboardResponse>(
      API_ENDPOINTS.GET_GAME_LEADERBOARD_WITH_CELEBRITIES(gameId),
      {
        params: { round_id: roundId, limit, offset },
      },
    ).then(handleResponseSuccess)
  },
  getPartnerGames: (
    partnerId: number | string,
    params?: { sport_name?: string; status?: GameStatusType; tournament_name?: string },
  ) => {
    return BEAPIClient.get<PartnerGamesResponse>(API_ENDPOINTS.GET_PARTNER_GAMES(partnerId), {
      params,
    }).then(handleResponseSuccess)
  },
  getPartnerGamesAuthed: (
    partnerId: number | string,
    params?: { sport_name?: string; tournament_name?: string },
  ) => {
    return AuthBEAPIClient.get<PartnerGamesResponse>(API_ENDPOINTS.GET_PARTNER_GAMES(partnerId), {
      params,
    }).then(handleResponseSuccess)
  },
  postCharityPageConsent: (
    gameId: number,
    partnerDomain: string,
    decision: CharityPageConsentDecision,
    buttonClicked: CharityPageConsentButtonClicked,
  ) => {
    return AuthBEAPIClient.post<void>(API_ENDPOINTS.POST_CHARITY_PAGE_CONSENT(gameId), {
      partner_domain: partnerDomain,
      decision,
      button_clicked: buttonClicked,
    }).then(handleResponseSuccess)
  },
}

export const profileService = {
  register: (data: RegisterDTO) => {
    return BEAPIClient.post<{ access_token: string; refresh_token: string }>(
      API_ENDPOINTS.REGISTER,
      data,
    ).then(handleResponseSuccess)
  },
  login: (email: string, password: string, organization_id: string) => {
    return BEAPIClient.post<{ access_token: string; refresh_token: string }>(API_ENDPOINTS.LOGIN, {
      email,
      password,
      organization_id,
    }).then(handleResponseSuccess)
  },
  resetPassword: (email: string, partner_id: string, organization_id: string) => {
    return BEAPIClient.post(
      API_ENDPOINTS.RESET_PASSWORD,
      { email },
      { params: { partner_id, organization_id } },
    ).then(handleResponseSuccess)
  },
  sendEmailVerificationOTP: (partner_id?: string) => {
    if (!partner_id) {
      return Promise.reject(new Error('Missing partner_id'))
    }
    return AuthBEAPIClient.post(API_ENDPOINTS.SEND_EMAIL_VERIFICATION_OTP, null, {
      params: { partner_id },
    }).then(handleResponseSuccess)
  },
  verifyEmailOTP: (otp: string) => {
    return AuthBEAPIClient.post(API_ENDPOINTS.VERIFY_EMAIL_OTP, null, { params: { otp } }).then(
      handleResponseSuccess,
    )
  },
  setPassword: (
    email: string,
    password: string,
    confirm_password: string,
    organization_id: string,
  ) => {
    return BEAPIClient.post(
      API_ENDPOINTS.SET_PASSWORD,
      { password, confirm_password },
      { params: { email, organization_id } },
    ).then(handleResponseSuccess)
  },
  getAddressByQuery: (address_query: string) => {
    return BEAPIClient.get<Addresses[]>(API_ENDPOINTS.GET_ADDRESS, {
      params: { address_query },
    })
  },

  getUserEmailPreferences: (game_id: number) => {
    return AuthBEAPIClient.get<z.infer<typeof UserEmailPreferencesValidation>>(
      API_ENDPOINTS.USER_EMAIL_PREFERENCES,
      {
        params: { game_id },
      },
    ).then(handleResponseSuccess)
  },

  updateUserEmailPreferences: (
    game_id: number,
    email_preferences: Partial<z.infer<typeof UserEmailPreferencesValidation>>,
  ) => {
    return AuthBEAPIClient.patch<{ OK: boolean }>(
      API_ENDPOINTS.USER_EMAIL_PREFERENCES,
      email_preferences,
      {
        params: { game_id },
      },
    )
  },
  externalLogin: (data: ExternalLoginRequest) => {
    return BEAPIClient.post<ExternalLoginResponse>(API_ENDPOINTS.EXTERNAL_LOGIN, data).then(
      handleResponseSuccess,
    )
  },
}

export const b2bService = {
  getWorkflow: (game_id: number, partner: string, gate_id: string) => {
    return AuthBEAPIClient.get<Workflow>(API_ENDPOINTS.GET_WORKFLOW, {
      params: {
        game_id,
        partner,
        gate_id,
      },
    }).then(handleResponseSuccess)
  },
  sendAnswers: (answers: Answer[]) => {
    return AuthBEAPIClient.post(API_ENDPOINTS.SEND_ANSWERS, answers).then(handleResponseSuccess)
  },
}

export const liveScoreService = {
  getGolfData: (unitId: number, matchId: number) => {
    return BEAPIClient.get<GolfData>(API_ENDPOINTS.GET_GOLF_DATA, {
      params: { unit_id: unitId, match_id: matchId },
    }).then(handleResponseSuccess)
  },
}
